/**
 * Shopify 图像序列拖拽控制模块
 * 支持鼠标拖拽、触摸、滚轮、键盘控制
 * 用于展示镜片撞击等产品动态效果
 */

class ImageSequenceViewer {
  constructor(container) {
    if (!container) {
      return;
    }

    this.container = container;
    this.imageSequence = [];
    this.totalFrames = 0;
    this.currentFrame = 0;
    this.isDragging = false;
    this.isProgressDragging = false;
    this.startX = 0;
    this.startFrame = 0;
    this.isLoaded = false;
    this.isAutoPlaying = false;
    this.autoPlayInterval = null;
    this.loadedImages = 0;
    this.preloadQueue = [];
    
    // 获取配置
    this.config = this.getConfig();
    
    // 获取DOM元素
    this.initElements();
    
    // 初始化
    this.init();
  }

  getConfig() {
    const dataset = this.container.dataset;
    return {
      totalFrames: parseInt(dataset.totalFrames) || 20,
      baseUrl: dataset.baseUrl || '',
      prefix: dataset.prefix || 'frame_',
      format: dataset.format || 'jpg',
      padding: parseInt(dataset.padding) || 3,
      autoplay: dataset.autoplay === 'true',
      autoplaySpeed: parseInt(dataset.autoplaySpeed) || 100,
      impactStart: parseInt(dataset.impactStart) || Math.floor(parseInt(dataset.totalFrames) * 0.4),
      impactEnd: parseInt(dataset.impactEnd) || Math.floor(parseInt(dataset.totalFrames) * 0.8)
    };
  }

  initElements() {
    this.imageFramesContainer = this.container.querySelector('.image-frames-container');
    this.loadingIndicator = this.container.querySelector('.loading-indicator');
    this.progressBar = this.container.querySelector('.progress-bar');
    this.progressTrack = this.container.querySelector('.progress-track');
    this.progressHandle = this.container.querySelector('.progress-handle');
    this.frameCounter = this.container.querySelector('.frame-counter');
    this.impactIndicator = this.container.querySelector('.impact-indicator');
    this.instructions = this.container.querySelector('.instructions');
  }

  init() {
    this.totalFrames = this.config.totalFrames;
    this.currentFrame = 0; // 确保初始帧为0
    
    // 立即显示控制栏，即使没有图像
    this.showControls();
    this.updateDisplay();
    
    if (!this.config.baseUrl) {
      this.showError('请配置图像序列基础URL');
      // 即使没有URL，也要初始化事件
      this.initEvents();
      return;
    }

    this.initEvents();
    this.loadImageSequence();
    
    // 自动播放
    if (this.config.autoplay) {
      setTimeout(() => {
        if (this.isLoaded) {
          this.startAutoPlay();
        }
      }, 1000);
    }
  }

  showControls() {
    // 确保控制栏可见
    const controls = this.container.querySelector('.sequence-controls');
    if (controls) {
      controls.style.display = 'block';
      controls.style.opacity = '1';
    }
    
    // 确保进度条手柄可见
    if (this.progressHandle) {
      this.progressHandle.style.display = 'flex';
      this.progressHandle.style.opacity = '1';
    }
  }

  initEvents() {
    // 鼠标拖拽事件
    this.container.addEventListener('mousedown', this.onMouseDown.bind(this));
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));

    // 触摸事件
    this.container.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
    document.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.onTouchEnd.bind(this));

    // 进度条事件
    if (this.progressBar) {
      this.progressBar.addEventListener('click', this.onProgressBarClick.bind(this));
      this.progressBar.addEventListener('mousedown', this.onProgressMouseDown.bind(this));
    }

    if (this.progressHandle) {
      this.progressHandle.addEventListener('mousedown', this.onHandleMouseDown.bind(this));
      this.progressHandle.addEventListener('touchstart', this.onHandleMouseDown.bind(this), { passive: false });
    }

    // 键盘控制
    document.addEventListener('keydown', this.onKeyDown.bind(this));

    // 鼠标滚轮控制
    this.container.addEventListener('wheel', this.onWheel.bind(this), { passive: false });

    // 窗口大小变化
    window.addEventListener('resize', this.debounce(this.onResize.bind(this), 250));

    // 可见性变化（性能优化）
    document.addEventListener('visibilitychange', this.onVisibilityChange.bind(this));
  }

  loadImageSequence() {
    this.showLoading();
    this.imageSequence = new Array(this.totalFrames);
    this.loadedImages = 0;

    // 创建所有图像元素的占位符
    this.createImageElements();

    // 预加载策略：先加载关键帧，然后加载其他帧
    const keyFrames = [0, Math.floor(this.totalFrames / 2), this.totalFrames - 1];
    const otherFrames = [];
    
    for (let i = 0; i < this.totalFrames; i++) {
      if (!keyFrames.includes(i)) {
        otherFrames.push(i);
      }
    }

    // 先加载关键帧
    this.loadFrames(keyFrames, () => {
      // 关键帧加载完成后，隐藏加载指示器并启用交互
      this.hideLoading();
      this.isLoaded = true;
      this.updateDisplay();
      
      // 继续加载其他帧
      this.loadFrames(otherFrames);
    });
  }

  loadFrames(frameIndices, callback) {
    let loadedCount = 0;
    const totalToLoad = frameIndices.length;

    if (totalToLoad === 0) {
      if (callback) callback();
      return;
    }

    frameIndices.forEach(index => {
      const img = new Image();
      const frameNumber = (index + 1).toString().padStart(this.config.padding, '0');
      
      // 确保URL正确构建
      let baseUrl = this.config.baseUrl;
      if (baseUrl && !baseUrl.endsWith('/')) {
        baseUrl += '/';
      }
      
      const imageSrc = `${baseUrl}${this.config.prefix}${frameNumber}.${this.config.format}`;
      
      img.onload = () => {
        this.imageSequence[index] = img;
        this.loadedImages++;
        loadedCount++;

        // 更新对应的DOM元素
        this.updateImageElement(index, img.src);

        if (loadedCount === totalToLoad && callback) {
          callback();
        }
      };

      img.onerror = () => {
        // 创建占位符图像
        const placeholderImg = this.createPlaceholderImage(index + 1);
        this.imageSequence[index] = placeholderImg;
        
        // 更新对应的DOM元素为占位符
        this.updateImageElement(index, placeholderImg.src);
        
        loadedCount++;
        
        if (loadedCount === totalToLoad && callback) {
          callback();
        }
      };

      img.src = imageSrc;
    });
  }

  createImageElements() {
    // 为所有帧创建图像元素（先创建占位符）
    for (let i = 0; i < this.totalFrames; i++) {
      const imgElement = document.createElement('img');
      imgElement.className = 'sequence-image';
      imgElement.dataset.frame = i;
      imgElement.alt = `Frame ${i + 1}`;
      
      // 创建临时占位符图像
      imgElement.src = this.createPlaceholderDataURL(i + 1);
      
      if (i === 0) {
        imgElement.classList.add('active');
      }
      
      this.imageFramesContainer.appendChild(imgElement);
    }

    this.imageElements = this.container.querySelectorAll('.sequence-image');
  }

  updateImageElement(frameIndex, imageSrc) {
    if (this.imageElements && this.imageElements[frameIndex]) {
      this.imageElements[frameIndex].src = imageSrc;
    }
  }

  createPlaceholderDataURL(frameNumber) {
    // 创建一个简单的SVG占位符
    const svg = `<svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="#1a1a1a"/>
      <text x="50%" y="50%" text-anchor="middle" dy=".3em" 
            font-family="Arial, sans-serif" font-size="16" fill="#666">
        Loading Frame ${frameNumber}...
      </text>
    </svg>`;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'block';
    }
  }

  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'none';
    }
  }

  showError(message) {
    if (this.loadingIndicator) {
      this.loadingIndicator.innerHTML = `
        <div style="color: #ff6b6b;">
          <div>⚠️</div>
          <div>${message}</div>
        </div>
      `;
    }
  }

  createPlaceholderImage(frameNumber) {
    // 创建SVG占位符图像
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    
    // 绘制占位符
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.fillStyle = '#ff6b6b';
    ctx.font = '48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`Frame ${frameNumber}`, canvas.width / 2, canvas.height / 2 - 20);
    
    ctx.fillStyle = '#ffffff';
    ctx.font = '24px Arial';
    ctx.fillText('图像加载失败', canvas.width / 2, canvas.height / 2 + 20);
    
    // 转换为图像对象
    const img = new Image();
    img.src = canvas.toDataURL();
    return img;
  }

  // 鼠标事件处理
  onMouseDown(e) {
    if (!this.isLoaded || this.isProgressDragging) return;
    
    this.isDragging = true;
    this.startX = e.clientX;
    this.startFrame = this.currentFrame;
    this.stopAutoPlay();
    e.preventDefault();
  }

  onMouseMove(e) {
    if (!this.isDragging || !this.isLoaded) return;

    const deltaX = e.clientX - this.startX;
    const sensitivity = Math.max(2, Math.floor(this.container.offsetWidth / this.totalFrames / 3));
    const frameChange = Math.floor(deltaX / sensitivity);
    const newFrame = Math.max(0, Math.min(this.totalFrames - 1, this.startFrame + frameChange));

    if (newFrame !== this.currentFrame) {
      this.setFrame(newFrame);
    }
  }

  onMouseUp() {
    this.isDragging = false;
  }

  // 触摸事件处理
  onTouchStart(e) {
    if (!this.isLoaded || this.isProgressDragging) return;
    
    this.isDragging = true;
    this.startX = e.touches[0].clientX;
    this.startFrame = this.currentFrame;
    this.stopAutoPlay();
    e.preventDefault();
  }

  onTouchMove(e) {
    if (!this.isDragging || !this.isLoaded) return;

    const deltaX = e.touches[0].clientX - this.startX;
    const sensitivity = Math.max(2, Math.floor(this.container.offsetWidth / this.totalFrames / 4));
    const frameChange = Math.floor(deltaX / sensitivity);
    const newFrame = Math.max(0, Math.min(this.totalFrames - 1, this.startFrame + frameChange));

    if (newFrame !== this.currentFrame) {
      this.setFrame(newFrame);
    }
    e.preventDefault();
  }

  onTouchEnd() {
    this.isDragging = false;
  }

  // 滚轮事件处理
  onWheel(e) {
    if (!this.isLoaded) return;
    
    e.preventDefault();
    this.stopAutoPlay();
    
    const delta = e.deltaY > 0 ? 1 : -1;
    const newFrame = Math.max(0, Math.min(this.totalFrames - 1, this.currentFrame + delta));
    this.setFrame(newFrame);
  }

  // 进度条事件处理
  onProgressBarClick(e) {
    if (!this.isLoaded || this.isProgressDragging) return;
    
    const rect = this.progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, clickX / rect.width));
    const newFrame = Math.round(progress * (this.totalFrames - 1));
    
    this.stopAutoPlay();
    this.setFrame(newFrame);
  }

  onProgressMouseDown(e) {
    if (!this.isLoaded) return;
    this.isProgressDragging = true;
    this.stopAutoPlay();
  }

  onHandleMouseDown(e) {
    if (!this.isLoaded) return;
    
    e.stopPropagation();
    e.preventDefault();
    this.isProgressDragging = true;
    this.stopAutoPlay();

    const handleDrag = (e) => {
      const rect = this.progressBar.getBoundingClientRect();
      const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      const progress = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const newFrame = Math.round(progress * (this.totalFrames - 1));
      this.setFrame(newFrame);
    };

    const handleEnd = () => {
      this.isProgressDragging = false;
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleDrag);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleDrag, { passive: false });
    document.addEventListener('touchend', handleEnd);
  }

  // 键盘事件处理
  onKeyDown(e) {
    if (!this.isLoaded) return;
    
    // 检查是否在输入框中
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    switch(e.key) {
      case 'ArrowLeft':
        this.stopAutoPlay();
        this.setFrame(Math.max(0, this.currentFrame - 1));
        e.preventDefault();
        break;
      case 'ArrowRight':
        this.stopAutoPlay();
        this.setFrame(Math.min(this.totalFrames - 1, this.currentFrame + 1));
        e.preventDefault();
        break;
      case ' ':
        if (this.isAutoPlaying) {
          this.stopAutoPlay();
        } else {
          this.startAutoPlay();
        }
        e.preventDefault();
        break;
      case 'Home':
        this.stopAutoPlay();
        this.setFrame(0);
        e.preventDefault();
        break;
      case 'End':
        this.stopAutoPlay();
        this.setFrame(this.totalFrames - 1);
        e.preventDefault();
        break;
    }
  }

  // 窗口大小变化处理
  onResize() {
    // 响应式调整可以在这里处理
    this.updateDisplay();
  }

  // 可见性变化处理（性能优化）
  onVisibilityChange() {
    if (document.hidden && this.isAutoPlaying) {
      this.stopAutoPlay();
    }
  }

  // 设置当前帧
  setFrame(frameIndex) {
    if (!this.isLoaded || frameIndex === this.currentFrame) return;
    
    this.currentFrame = Math.max(0, Math.min(this.totalFrames - 1, frameIndex));
    this.updateDisplay();
  }

  // 更新显示
  updateDisplay() {
    // 总是更新进度条
    this.updateProgressBar();
    
    if (!this.isLoaded) {
      return;
    }

    // 更新图像显示
    if (this.imageElements) {
      this.imageElements.forEach((img, index) => {
        img.classList.toggle('active', index === this.currentFrame);
      });
    }

    // 更新帧计数器（如果存在）
    if (this.frameCounter) {
      this.frameCounter.textContent = `帧: ${this.currentFrame + 1}/${this.totalFrames}`;
    }

    // 更新撞击指示器
    if (this.impactIndicator) {
      const showImpact = this.currentFrame >= this.config.impactStart && 
                        this.currentFrame <= this.config.impactEnd;
      this.impactIndicator.classList.toggle('active', showImpact);
    }

    // 触发自定义事件
    this.container.dispatchEvent(new CustomEvent('frameChanged', {
      detail: {
        currentFrame: this.currentFrame,
        totalFrames: this.totalFrames,
        progress: this.totalFrames > 1 ? this.currentFrame / (this.totalFrames - 1) : 0
      }
    }));
  }

  updateProgressBar() {
    if (this.progressHandle) {
      const progress = this.totalFrames > 1 ? this.currentFrame / (this.totalFrames - 1) : 0;
      const progressPercent = Math.max(0, Math.min(100, progress * 100));
      
      // 更新进度轨道宽度
      if (this.progressTrack) {
        this.progressTrack.style.width = `${progressPercent}%`;
      }
      
      // 计算手柄位置，确保不超出进度条范围
      const handleOffset = Math.max(0, Math.min(100, progressPercent));
      this.progressHandle.style.left = `${handleOffset}%`;
      
      // 确保手柄可见
      this.progressHandle.style.display = 'flex';
      this.progressHandle.style.opacity = '1';
    }
  }

  // 自动播放控制
  startAutoPlay() {
    if (this.isAutoPlaying || !this.isLoaded) return;
    
    this.isAutoPlaying = true;
    this.autoPlayInterval = setInterval(() => {
      const nextFrame = (this.currentFrame + 1) % this.totalFrames;
      this.setFrame(nextFrame);
    }, this.config.autoplaySpeed);

    // 隐藏说明文字
    if (this.instructions) {
      this.instructions.style.opacity = '0.3';
    }
  }

  stopAutoPlay() {
    if (!this.isAutoPlaying) return;
    
    this.isAutoPlaying = false;
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }

    // 显示说明文字
    if (this.instructions) {
      this.instructions.style.opacity = '';
    }
  }

  // 播放完整动画
  playFullAnimation(callback) {
    if (!this.isLoaded) return;
    
    this.stopAutoPlay();
    let frame = 0;
    
    const animate = () => {
      this.setFrame(frame);
      frame++;
      
      if (frame < this.totalFrames) {
        setTimeout(animate, this.config.autoplaySpeed);
      } else if (callback) {
        callback();
      }
    };
    
    animate();
  }

  // 工具函数：防抖
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // 公共API方法
  getCurrentFrame() {
    return this.currentFrame;
  }

  getTotalFrames() {
    return this.totalFrames;
  }

  getProgress() {
    return this.totalFrames > 1 ? this.currentFrame / (this.totalFrames - 1) : 0;
  }

  isPlaying() {
    return this.isAutoPlaying;
  }

  // 销毁方法
  destroy() {
    this.stopAutoPlay();
    
    // 移除事件监听器
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('touchmove', this.onTouchMove);
    document.removeEventListener('touchend', this.onTouchEnd);
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    
    // 清理DOM
    if (this.imageFramesContainer) {
      this.imageFramesContainer.innerHTML = '';
    }
    
    // 清理引用
    this.imageSequence = null;
    this.imageElements = null;
  }
}

// 全局初始化函数
window.ImageSequenceViewer = ImageSequenceViewer;

// 自动初始化页面上的所有序列查看器
document.addEventListener('DOMContentLoaded', function() {
  const viewers = document.querySelectorAll('.sequence-viewer');
  
  viewers.forEach((viewer, index) => {
    if (!viewer.dataset.initialized) {
      try {
        const instance = new ImageSequenceViewer(viewer);
        viewer.sequenceViewer = instance; // 保存实例引用
        viewer.dataset.initialized = 'true';
      } catch (error) {
        // 静默处理错误
      }
    }
  });
});

// 备用初始化（防止DOMContentLoaded已经触发）
if (document.readyState === 'loading') {
  // DOM还在加载中，等待DOMContentLoaded
} else {
  // DOM已经加载完成，立即初始化
  setTimeout(() => {
    const viewers = document.querySelectorAll('.sequence-viewer:not([data-initialized])');
    viewers.forEach((viewer, index) => {
      try {
        const instance = new ImageSequenceViewer(viewer);
        viewer.sequenceViewer = instance;
        viewer.dataset.initialized = 'true';
      } catch (error) {
        // 静默处理错误
      }
    });
  }, 100);
}

// Shopify Section 重新加载支持
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const viewer = event.target.querySelector('.sequence-viewer');
    if (viewer && !viewer.dataset.initialized) {
      new ImageSequenceViewer(viewer);
      viewer.dataset.initialized = 'true';
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    const viewer = event.target.querySelector('.sequence-viewer');
    if (viewer && viewer.sequenceViewer) {
      viewer.sequenceViewer.destroy();
    }
  });
}
